import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ConvertedHoliday } from '../types';

import { schema } from '../utils/schema';
import { convertPayloadForSave } from '../utils/utils';

import { HOLIDAYS } from 'RestAPIs/endpoints';
import { useSaveResource } from 'RestAPIs/useSaveResource';

import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';

import { ControlledDatePicker, ControlledTextField } from 'components/shared';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

type CreateEditHolidayFormProps = {
  handleClose: () => void;
  rowData?: ConvertedHoliday;
};

const CreateEditHolidayForm = ({
  handleClose,
  rowData
}: CreateEditHolidayFormProps) => {
  const { t } = useTranslation();
  const { mutateAsync: saveForm, isLoading } = useSaveResource({
    url: HOLIDAYS,
    id: rowData?.holidayId,
    invalidateQueryKey: ['holidays', 'list']
  });

  const methods = useForm({
    defaultValues: schema.cast(rowData),
    resolver: yupResolver(schema)
  });
  const { control, handleSubmit } = methods;

  const onSubmit = (data) => {
    const payload = convertPayloadForSave(data);
    saveForm(payload).then(handleClose);
  };

  return (
    <>
      <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
        <MultiStepForm.Section
          title={t('basis')}
          validateFields={['date', 'description', 'holidayCompLeaveHours']}
        >
          <Box display="flex" gap={2.5}>
            <ControlledDatePicker
              control={control}
              name="date"
              variant="outlined"
              label={t('date')}
              fullWidth
            />
          </Box>
          <Box display="flex" gap={2.5}>
            <ControlledTextField
              name="description"
              type="string"
              control={control}
              variant="outlined"
              label={t('holidays.grid.description')}
              inputProps={{ min: 0, max: 9999.99 }}
            />
          </Box>
          <Box display="flex" gap={2.5}>
            <ControlledTextField
              name="holidayCompLeaveHours"
              type="number"
              control={control}
              variant="outlined"
              label={t('holidays.grid.holidayCompLeaveHours')}
              inputProps={{ min: 0, max: 9999.99 }}
            />
          </Box>
        </MultiStepForm.Section>

        <MultiStepForm.Actions>
          <Box display="flex" ml="auto">
            <LoadingButton
              loading={isLoading}
              type="submit"
              variant="contained"
              autoFocus
              color="primary"
            >
              {t('save')}
            </LoadingButton>
          </Box>
        </MultiStepForm.Actions>
      </MultiStepForm>
    </>
  );
};

export default CreateEditHolidayForm;
